import { useState, useCallback, useRef, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from 'react-query'
import { useNavigate } from 'react-router-dom'
import { useArcTranslation, useMantineTheme, useMediaQuery, useFeatureToggle, hasPermission } from 'hooks'
import {
  Group, Button, Text, Divider, Grid, Title, Badge, CopyButton, Stack,
  NewProjectActionMenu, AssignProjectModal, EditStudentsModal, MainTeacherBlock,
} from 'components'
import { ShareWithTeachersModal } from '../../pages/ArcProjectsList/components/ShareWithTeachersModal'
import { ArcProjectInfoMeta } from './ArcProjectInfoMeta'
import { AddToMyProjectsModal } from './AddToMyProjectsModal'
import type { AssignProjectData } from 'components'
import { IconClipboard, IconClipboardCheck, IconUsers, IconUserPlus, IconCheck, IconRotateClockwise2 } from '@tabler/icons-react'
import { notifications } from 'context'
import { ProjectHeaderProps } from './intefaces'
import { assignProject, assignPublishedTemplate } from '../../pages/ArcProjectsList/services/arcProjectsApi'
import dayjs from 'dayjs'

// AR-857 (HIG): how long the success state stays visible in the modal
// before it closes and the app navigates to the new project
const ADD_SUCCESS_FEEDBACK_MS = 900

export const ProjectHeader = ({
  project,
  teachers,
  lessonsCount,
  handleEdit,
  handleRemove,
  handlePublish,
  handleApprove,
  handleReopen,
  handleFinish,
}: ProjectHeaderProps) => {
  const { t } = useArcTranslation()
  const { i18n } = useTranslation()
  const theme = useMantineTheme()
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const isMobile = useMediaQuery(`(max-width: ${theme.breakpoints.sm})`)
  const isArcMosaico = useFeatureToggle('arc-mosaico-project') as boolean

  const [assignModalOpened, setAssignModalOpened] = useState(false)
  const [editStudentsModalOpened, setEditStudentsModalOpened] = useState(false)
  const [isAssigning, setIsAssigning] = useState(false)
  const [shareModalOpened, setShareModalOpened] = useState(false)
  const [addToMyProjectsOpened, setAddToMyProjectsOpened] = useState(false)
  const [addSucceeded, setAddSucceeded] = useState(false)
  const successTimerRef = useRef<number>()

  useEffect(() => () => window.clearTimeout(successTimerRef.current), [])

  const isTemplate = project?.kind === 'template'
  const isApplication = project?.kind === 'application'
  const isDraft = project?.status === 'draft'
  const isPublished = project?.status === 'published'
  const isApproved = project?.status === 'approved'
  const isFinished = project?.status === 'finished'

  // Arc Mosaico: draft templates can be assigned (opens modal)
  const canAssignDraft = isArcMosaico && isTemplate && isDraft
  // Arc Mosaico: published templates can be added to my projects (no modal)
  const canAddToMyProjects = isArcMosaico && isTemplate && isPublished

  // Arc Mosaico: tutors and managers can complete assigned (approved) application projects
  const isTutorOrManager = hasPermission(['tutor', 'manager'])
  const canComplete = isArcMosaico && isTutorOrManager && isApplication && isApproved
  // Arc Mosaico: tutors and managers can reopen finished application projects
  const canReopen = isArcMosaico && isTutorOrManager && isApplication && isFinished
  // Arc Mosaico: tutors and managers can edit assigned students on approved projects
  const canEditStudents = isArcMosaico && isTutorOrManager && isApproved

  const openAssignModal = useCallback(() => setAssignModalOpened(true), [])
  const closeAssignModal = useCallback(() => {
    if (!isAssigning) setAssignModalOpened(false)
  }, [isAssigning])

  // AR-857: open the confirmation modal; it stays mounted below with the
  // confirm button loading until the template copy finishes
  const handleAddToMyProjects = useCallback(() => {
    if (!project?.id) return
    setAddToMyProjectsOpened(true)
  }, [project?.id])

  const closeAddToMyProjects = useCallback(() => {
    if (!isAssigning && !addSucceeded) setAddToMyProjectsOpened(false)
  }, [isAssigning, addSucceeded])

  const handleConfirmAddToMyProjects = useCallback(() => {
    if (!project?.id) return

    setIsAssigning(true)

    // The backend copies the template synchronously, so once the request
    // resolves with a projectId the new project is ready to open
    assignPublishedTemplate(project.id).then(response => {
      if (response.success && response.projectId) {
        const newProjectId = response.projectId
        setIsAssigning(false)
        // HIG: acknowledge completion in place, then transition — the modal
        // shows its success state briefly before fading out and navigating
        setAddSucceeded(true)
        queryClient.invalidateQueries(['arc-projects'])
        successTimerRef.current = window.setTimeout(() => {
          setAddToMyProjectsOpened(false)
          setAddSucceeded(false)
          notifications.show({
            title: t('arc_projects.add_project_success_title'),
            message: t('arc_projects.add_project_success_message'),
            color: 'green',
            icon: <IconCheck size={18} />,
          })
          // Router navigation keeps the persistent Arc shell mounted; the
          // project view fetches its own fresh data on mount. The state flag
          // tells the lessons view that this project was just copied, so it
          // polls until the meetings/activities job has populated them.
          navigate(`/projects/${newProjectId}`, { state: { arcTemplateCopy: true } })
        }, ADD_SUCCESS_FEEDBACK_MS)
      } else {
        setIsAssigning(false)
        const errorMessage = response.error === 'UNAUTHORIZED'
          ? t('arc_projects.assign_unauthorized')
          : t('arc_projects.assign_error')
        notifications.show({ title: t('general_error_title'), message: errorMessage, color: 'red' })
      }
    }).catch(() => {
      setIsAssigning(false)
      notifications.show({ title: t('general_error_title'), message: t('arc_projects.assign_error'), color: 'red' })
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps -- t from useArcTranslation is unstable
  }, [project?.id, queryClient, navigate])

  const handleAssignSave = useCallback(async (data: AssignProjectData) => {
    if (!data.groupId || data.assignedStudentIds.length === 0) {
      notifications.show({
        title: t('general_error_title'),
        message: t('arc_projects.assign_validation_error'),
        color: 'red',
      })
      return
    }

    setIsAssigning(true)

    try {
      const response = await assignProject(project.id, data.groupId, data.assignedStudentIds, data.startDate)

      if (response.success && response.project) {
        notifications.show({ message: t('arc_projects.assign_success'), color: 'green' })
        queryClient.invalidateQueries(['arc-projects'])
        setAssignModalOpened(false)
        // Reload to show updated project status
        window.location.reload()
      } else {
        const errorMessage = response.error === 'UNAUTHORIZED'
          ? t('arc_projects.assign_unauthorized')
          : response.error || t('arc_projects.assign_error')
        notifications.show({ title: t('general_error_title'), message: errorMessage, color: 'red' })
      }
    } catch (error: unknown) {
      const axiosError = error as { response?: { data?: { error?: string; message?: string } } }
      const errorMessage = axiosError.response?.data?.error
        || axiosError.response?.data?.message
        || t('arc_projects.assign_error')

      notifications.show({ title: t('general_error_title'), message: errorMessage, color: 'red' })
    } finally {
      setIsAssigning(false)
    }
  }, [project?.id, t, queryClient])

  const closeEditStudents = useCallback(() => {
    setEditStudentsModalOpened(false)
    queryClient.invalidateQueries(['arc-projects'])
  }, [queryClient])

  // AR-882: prefer the unique-lessons count from the session-meetings list;
  // project.totalMeetings counts activities (not lessons) for students
  const meetingsCount = lessonsCount ?? (project && project.totalMeetings ? project.totalMeetings : 0)
  const meetingText =
    meetingsCount && meetingsCount === 1 ? t('new_project_view.lesson') : t('new_project_view.lessons')

  const updatedAt =
    project && project.updatedAt
      ? i18n.language === 'pt-BR'
        ? dayjs(project.updatedAt).format('DD/MM/YYYY HH:mm')
        : dayjs(project.updatedAt).format('MM/DD/YYYY HH:mm')
      : ''

  return (
    <>
      {project !== null && project.id !== '' && (
        <>
          <Grid>
            <Grid.Col span={12} xs={12} sm={7}>
              <Stack spacing='sm'>
                <Stack spacing={0} align='flex-start'>
                  <Title order={1}>{project.name}</Title>
                  <Group spacing='xs'>
                    {!isArcMosaico && project.kind === 'template' && (
                      <Badge color='orange' variant='filled'>
                        {t('new_project_view.badge_template')}
                      </Badge>
                    )}
                    {project.status === 'draft' && (
                      <Badge color='gray' variant='filled'>
                        {t('new_project_view.badge_draft')}
                      </Badge>
                    )}
                    {project.status === 'approved' && (
                      <Badge
                        color='blue'
                        variant={isArcMosaico ? 'light' : 'filled'}
                        size={isArcMosaico ? 'lg' : undefined}
                      >
                        {t('arc_projects.status_assigned')}
                      </Badge>
                    )}
                    {project.status === 'finished' && (
                      <Badge
                        color='green'
                        variant={isArcMosaico ? 'light' : 'filled'}
                        size={isArcMosaico ? 'lg' : undefined}
                      >
                        {t('new_project_view.badge_finished')}
                      </Badge>
                    )}
                  </Group>
                </Stack>
                {!isArcMosaico && (
                  <Text size='sm' color='dimmed'>
                    {t('new_project_view.label_last_update')} {updatedAt}
                  </Text>
                )}
              </Stack>
            </Grid.Col>

            <Grid.Col span={12} xs={12} sm={5}>
              <Group spacing='sm' position='right'>
                {!isArcMosaico && (
                  <CopyButton value={window.location.href}>
                    {({ copied, copy }) => (
                      <Button
                        size='md'
                        variant='outline'
                        color={copied ? 'green' : ''}
                        onClick={copy}
                        fullWidth={isMobile}
                        leftIcon={copied ? <IconClipboardCheck /> : <IconClipboard />}
                      >
                        {copied ? t('general_buttons_copied_url') : t('general_buttons_copy_url')}
                      </Button>
                    )}
                  </CopyButton>
                )}
                <NewProjectActionMenu
                  project={project}
                  handleEdit={handleEdit}
                  handleRemove={handleRemove}
                  handlePublish={handlePublish}
                  handleApprove={handleApprove}
                  handleReopen={handleReopen}
                  handleFinish={handleFinish}
                  onShareWithTeachers={() => setShareModalOpened(true)}
                  onEditStudents={() => setEditStudentsModalOpened(true)}
                />
                {canAssignDraft && (
                  <Button
                    size='md'
                    variant='filled'
                    color='teal'
                    onClick={openAssignModal}
                    fullWidth={isMobile}
                    leftIcon={<IconUserPlus size={18} />}
                    loading={isAssigning}
                  >
                    {t(isTemplate && isPublished ? 'arc_projects.assign_project_library' : 'arc_projects.assign_project')}
                  </Button>
                )}
                {canAddToMyProjects && (
                  <Button
                    size='md'
                    variant='filled'
                    color='teal'
                    onClick={handleAddToMyProjects}
                    fullWidth={isMobile}
                    leftIcon={<IconUserPlus size={18} />}
                    loading={isAssigning}
                  >
                    {t('arc_projects.assign_project_library')}
                  </Button>
                )}
                {canComplete && (
                  <Button
                    size='md'
                    variant='filled'
                    color='teal'
                    onClick={() => handleFinish(project.id)}
                    fullWidth={isMobile}
                    leftIcon={<IconCheck size={18} />}
                  >
                    {t('arc_projects.complete_project')}
                  </Button>
                )}
                {canReopen && (
                  <Button
                    size='md'
                    variant='filled'
                    color='teal'
                    onClick={() => handleReopen(project.id)}
                    fullWidth={isMobile}
                    leftIcon={<IconRotateClockwise2 size={18} />}
                  >
                    {t('arc_projects.reopen_project')}
                  </Button>
                )}
              </Group>
            </Grid.Col>
          </Grid>
          <Divider mt='md' mb='md' />
          {isArcMosaico ? (
            <ArcProjectInfoMeta project={project} teachers={teachers} lessonsCount={lessonsCount} />
          ) : (
            <Grid columns={12}>
              <Grid.Col span={12} sm={6} md={2}>
                <Text size='sm' color='dimmed'>
                  {t('new_project_view.label_project_type')}
                </Text>
                <Text>{t(`modality.${project.modality}`)}</Text>
              </Grid.Col>
              <Grid.Col span={12} sm={6} md={project.kind !== 'template' ? 2 : 'auto'}>
                <Text size='sm' color='dimmed'>
                  {t('new_project_view.label_project_lessons')}
                </Text>
                <Text>
                  {meetingsCount} {meetingText}
                </Text>
              </Grid.Col>
              {project.kind !== 'template' && (
                <Grid.Col span={12} sm={6} md='auto'>
                  <Text size='sm' color='dimmed'>
                    {t('new_project_view.label_group')}
                  </Text>
                  <Text>{project.groupName !== null ? project.groupName : t('new_project_view.no_group_added')}</Text>
                </Grid.Col>
              )}
              <Grid.Col span={12} sm={6} md='content'>
                {project.tutorMain && (
                  <MainTeacherBlock
                    teachers={teachers}
                    mainTeacher={project.tutorMain}
                    hideRole={false}
                  />
                )}
              </Grid.Col>
            </Grid>
          )}
        </>
      )}

      {isArcMosaico && project && (
        <>
          <AssignProjectModal
            opened={assignModalOpened}
            onClose={closeAssignModal}
            onSave={handleAssignSave}
            projectId={project.id}
            projectName={project.name}
            isLoading={isAssigning}
          />
          <EditStudentsModal
            opened={editStudentsModalOpened}
            onClose={closeEditStudents}
            projectId={project.id}
            schoolTermId={project.schoolTermId || ''}
            groupName={project.groupName || ''}
          />
          <ShareWithTeachersModal
            opened={shareModalOpened}
            onClose={() => setShareModalOpened(false)}
            projectId={project.id}
            copyOnly={project.status === 'finished'}
          />
          <AddToMyProjectsModal
            opened={addToMyProjectsOpened}
            loading={isAssigning}
            success={addSucceeded}
            onClose={closeAddToMyProjects}
            onConfirm={handleConfirmAddToMyProjects}
          />
        </>
      )}
    </>
  )
}
